import React, { useEffect, useRef, useState } from 'react';

/**
 * LazyMount
 * Renders `fallback` (a plain div reserving `minHeight`, so nothing
 * shifts when the real content swaps in — keeps CLS low) until the
 * wrapper scrolls within `rootMargin` of the viewport, then renders
 * `children`. Pair with React.lazy() for components whose *code* should
 * also not be fetched until the section is actually about to be seen
 * (e.g. the three.js/d3-powered globe, which is heavy and below the
 * fold on every viewport this site targets).
 */
const LazyMount = ({ children, minHeight = 480, rootMargin = '400px' }) => {
  const ref = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (shouldRender) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setShouldRender(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [shouldRender, rootMargin]);

  if (!shouldRender) {
    return <div ref={ref} style={{ minHeight }} aria-hidden="true" />;
  }

  return <div ref={ref}>{children}</div>;
};

export default LazyMount;
